import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Check, ChevronsUpDown, Loader2, Pencil, Plus, Sparkles, Wand2, Wrench, X } from 'lucide-react';
import { MarkdownEditor } from 'components/blocks/markdown-editor';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Textarea } from 'components/ui/textarea';
import { useGetTools } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useConfig } from 'hooks/use-config';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { SkillListItem, type SkillVisibility } from './skills.types';
import { SkillVisibilityField } from './skill-visibility';
import { useCreateSkill, useDraftSkill, useOptimizeSkillField, useUpdateSkill } from './use-skills';

const createSkillSchema = (t: (key: string, options?: Record<string, unknown>) => string) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, t('CREATE_SKILL_NAME_REQUIRED') || 'Skill name is required')
      .max(64, t('CREATE_SKILL_NAME_MAX') || 'Maximum 64 characters'),
    displayName: z.string().trim().max(128, t('CREATE_SKILL_DISPLAY_NAME_MAX') || 'Maximum 128 characters'),
    description: z
      .string()
      .trim()
      .min(1, t('CREATE_SKILL_DESCRIPTION_REQUIRED') || 'Description is required')
      .max(1024, t('CREATE_SKILL_DESCRIPTION_MAX') || 'Maximum 1024 characters'),
    instructions: z
      .string()
      .trim()
      .min(1, t('CREATE_SKILL_INSTRUCTIONS_REQUIRED') || 'Instructions are required')
      .max(50000, t('CREATE_SKILL_INSTRUCTIONS_MAX') || 'Maximum 50000 characters'),
    tags: z.string().trim(),
    requiredToolIds: z.array(z.string()),
    visibility: z.enum(['private', 'shared', 'public']),
  });

type CreateSkillForm = z.infer<ReturnType<typeof createSkillSchema>>;

const DEFAULT_VALUES: CreateSkillForm = {
  name: '',
  displayName: '',
  description: '',
  instructions: '',
  tags: '',
  requiredToolIds: [],
  visibility: 'private',
};

const DEFAULT_SCOPE = 'personal';

const getSkillFormVisibility = (visibility?: string | null): SkillVisibility =>
  visibility === 'public' || visibility === 'private' || visibility === 'shared' ? visibility : 'public';

const parseTags = (value: string) =>
  value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const toKebabCase = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const getErrorDetailMessage = (
  error: unknown,
  translate: (key: string) => string
) => {
  if (error && typeof error === 'object') {
    const maybeError = error as {
      error?: unknown;
      detail?: unknown;
      message?: unknown;
    };

    if (maybeError.error && typeof maybeError.error === 'object') {
      const nestedError = maybeError.error as { detail?: unknown };
      if (typeof nestedError.detail === 'string' && nestedError.detail.trim()) {
        return nestedError.detail;
      }
    }

    if (typeof maybeError.detail === 'string' && maybeError.detail.trim()) {
      return maybeError.detail;
    }

    if (typeof maybeError.message === 'string' && maybeError.message.trim()) {
      try {
        const parsedMessage = JSON.parse(maybeError.message) as { detail?: unknown };
        if (typeof parsedMessage.detail === 'string' && parsedMessage.detail.trim()) {
          return parsedMessage.detail;
        }
      } catch {
        return maybeError.message;
      }
    }
  }

  return translate('PLEASE_TRY_AGAIN') || 'Please try again.';
};

const getFormValues = (skill?: SkillListItem | null): CreateSkillForm => ({
  name: skill?.name || '',
  displayName: skill?.displayName || '',
  description: skill?.description || '',
  instructions: skill?.instructions || '',
  tags: skill?.tags?.join(', ') || '',
  requiredToolIds: skill?.requiredToolIds || [],
  visibility: getSkillFormVisibility(skill?.visibility),
});

const getToolTypeLabel = (
  type: ITool['type'],
  translate: (key: string) => string
) =>
  type === 'mcp_server'
    ? translate('CREATE_SKILL_TOOL_TYPE_MCP') || 'MCP Server'
    : translate('CREATE_SKILL_TOOL_TYPE_API') || 'API Tool';

export function CreateSkillModal({
  open,
  onOpenChange,
  skill,
  startInDraftMode = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill?: SkillListItem | null;
  /** Open straight into the one-line "Generate a draft" prompt (create only). */
  startInDraftMode?: boolean;
}) {
  const { t } = useTranslation();
  const isEditing = !!skill;
  const isOwner = useIsOwner(skill);
  const canEditSkill = !isEditing || isOwner;
  const { toast } = useToast();
  const skillSchema = useMemo(() => createSkillSchema(t), [t]);
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [activeToolTab, setActiveToolTab] = useState<'api' | 'mcp_server'>('api');
  const [hasOpenedMcpTab, setHasOpenedMcpTab] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState('');
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const { mutateAsync: createSkill, isPending: isCreating } = useCreateSkill();
  const { mutateAsync: updateSkill, isPending: isUpdating } = useUpdateSkill();
  const { mutateAsync: draftSkill, isPending: isDrafting } = useDraftSkill();
  const { mutateAsync: optimizeField, isPending: isOptimizing } = useOptimizeSkillField();
  const [optimizingField, setOptimizingField] = useState<'description' | 'instructions' | null>(null);
  const { data: apiToolsData, isLoading: isApiToolsLoading } = useGetTools({
    tool_type: 'api',
    page: 1,
    page_size: 50,
    project_key: blocksKey,
  });
  const { data: mcpToolsData, isLoading: isMcpToolsLoading } = useGetTools(
    {
      tool_type: 'mcp_server',
      page: 1,
      page_size: 50,
      project_key: blocksKey,
    },
    { enabled: hasOpenedMcpTab }
  );

  const form = useForm<CreateSkillForm>({
    resolver: zodResolver(skillSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const allTools = useMemo<ITool[]>(() => {
    const uniqueTools = new Map<string, ITool>();

    [...(apiToolsData?.tools || []), ...(mcpToolsData?.tools || [])].forEach((tool) => {
      uniqueTools.set(tool.id, tool);
    });

    return Array.from(uniqueTools.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [apiToolsData?.tools, mcpToolsData?.tools]);

  const apiTools = useMemo(
    () => [...(apiToolsData?.tools || [])].sort((left, right) => left.name.localeCompare(right.name)),
    [apiToolsData?.tools]
  );

  const mcpTools = useMemo(
    () => [...(mcpToolsData?.tools || [])].sort((left, right) => left.name.localeCompare(right.name)),
    [mcpToolsData?.tools]
  );

  const toolsById = useMemo(() => new Map(allTools.map((tool) => [tool.id, tool])), [allTools]);
  const visibleTools = activeToolTab === 'api' ? apiTools : mcpTools;

  useEffect(() => {
    if (activeToolTab === 'mcp_server' && !hasOpenedMcpTab) {
      setHasOpenedMcpTab(true);
    }
  }, [activeToolTab, hasOpenedMcpTab]);

  useEffect(() => {
    if (open) {
      form.reset(getFormValues(skill));
      setShowDraftPrompt(!isEditing && startInDraftMode);
      setDraftPrompt('');
      return;
    }

    form.reset(DEFAULT_VALUES);
    setIsToolsOpen(false);
    setActiveToolTab('api');
    setHasOpenedMcpTab(false);
    setShowDraftPrompt(false);
    setDraftPrompt('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, open, skill]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      form.reset(DEFAULT_VALUES);
      setIsToolsOpen(false);
      setActiveToolTab('api');
      setHasOpenedMcpTab(false);
      setShowDraftPrompt(false);
      setDraftPrompt('');
    }
  };

  const handleGenerateDraft = async () => {
    const prompt = draftPrompt.trim();
    if (!prompt) return;
    try {
      const draft = await draftSkill(prompt);
      form.reset({
        name: draft.name ? toKebabCase(draft.name) : form.getValues('name'),
        displayName: draft.name || form.getValues('displayName'),
        description: draft.description || form.getValues('description'),
        instructions: draft.instructions || form.getValues('instructions'),
        tags: draft.tags?.length ? draft.tags.join(', ') : form.getValues('tags'),
        requiredToolIds: form.getValues('requiredToolIds'),
        visibility: form.getValues('visibility'),
      });
      setShowDraftPrompt(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('SKILL_DRAFT_FAILED') || 'Failed to generate draft',
        description: getErrorDetailMessage(error, t),
      });
    }
  };

  const handleOptimizeField = async (field: 'description' | 'instructions') => {
    const value = form.getValues(field).trim();
    if (!value) return;
    setOptimizingField(field);
    try {
      const otherField = field === 'description' ? 'instructions' : 'description';
      const result = await optimizeField({
        field,
        value,
        context: { [otherField]: form.getValues(otherField) },
      });
      form.setValue(field, result.value, { shouldDirty: true });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('SKILL_OPTIMIZE_FAILED') || 'Failed to optimize with AI',
        description: getErrorDetailMessage(error, t),
      });
    } finally {
      setOptimizingField(null);
    }
  };

  const onSubmit = async (values: CreateSkillForm) => {
    try {
      if (isEditing && skill) {
        if (!canEditSkill) {
          toast({
            variant: 'destructive',
            title: t('CREATE_SKILL_TOAST_UPDATE_FAILED_TITLE') || 'Failed to update skill',
            description: t('SKILL_EDIT_PERMISSION_DENIED') || 'You can only edit skills you created.',
          });
          return;
        }

        await updateSkill({
          skillId: skill.id,
          payload: {
            name: toKebabCase(values.name.trim()),
            displayName: values.displayName.trim() || undefined,
            description: values.description.trim(),
            instructions: values.instructions.trim(),
            version: skill.version || '1.0.0',
            iconUrl: skill.iconUrl,
            tags: parseTags(values.tags),
            requiredToolIds: values.requiredToolIds,
            requiredIntegrationTypes: skill.requiredIntegrationTypes || [],
            scope: (skill.scope as 'personal') || DEFAULT_SCOPE,
            visibility: values.visibility,
          },
        });

        toast({
          variant: 'success',
          title: t('CREATE_SKILL_TOAST_UPDATED_TITLE') || 'Skill updated',
          description:
            t('CREATE_SKILL_TOAST_UPDATED_DESCRIPTION') ||
            'Your skill has been updated successfully.',
        });
      } else {
        await createSkill({
          name: toKebabCase(values.name.trim()),
          displayName: values.displayName.trim() || undefined,
          description: values.description.trim(),
          instructions: values.instructions.trim(),
          tags: parseTags(values.tags),
          requiredToolIds: values.requiredToolIds,
          requiredIntegrationTypes: [],
          scope: DEFAULT_SCOPE,
          visibility: values.visibility,
        });

        toast({
          variant: 'success',
          title: t('CREATE_SKILL_TOAST_CREATED_TITLE') || 'Skill created',
          description:
            t('CREATE_SKILL_TOAST_CREATED_DESCRIPTION') ||
            'Your skill has been saved successfully.',
        });
      }

      handleOpenChange(false);
    } catch (error) {
      toast({
        title: isEditing
          ? t('CREATE_SKILL_TOAST_UPDATE_FAILED') || 'Failed to update skill'
          : t('CREATE_SKILL_TOAST_CREATE_FAILED') || 'Failed to create skill',
        description: getErrorDetailMessage(error, t),
        variant: 'destructive',
      });
    }
  };

  const isPending = isCreating || isUpdating;
  const isVisibleToolsLoading = activeToolTab === 'api' ? isApiToolsLoading : isMcpToolsLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] w-[96vw] max-w-5xl flex-col overflow-hidden rounded-[0.5rem] border-0 p-0 shadow-xl"
        hideClose
        aria-describedby={undefined}
      >
        <DialogHeader className="px-4 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-lg font-medium leading-none text-foreground">
                {isEditing
                  ? t('CREATE_SKILL_TITLE_EDIT') || 'Edit skill instructions'
                  : t('CREATE_SKILL_TITLE_CREATE') || 'Write skill instructions'}
              </DialogTitle>
              <p className="mt-2 text-[13px] leading-6 text-muted-foreground">
                {isEditing
                  ? t('CREATE_SKILL_SUBTITLE_EDIT') ||
                  'Update the description, tags, and markdown instructions for this skill.'
                  : t('CREATE_SKILL_SUBTITLE_CREATE') ||
                  'Create a reusable skill with a clear description and markdown-based instructions.'}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-neutral-500 hover:bg-transparent hover:text-neutral-500 focus-visible:ring-0 dark:text-neutral-400 dark:hover:bg-transparent dark:hover:text-neutral-400"
              onClick={() => handleOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {showDraftPrompt ? (
          <div className="flex flex-col gap-3 border-b border-border/70 px-4 py-4">
            <label
              htmlFor="skill-draft-prompt"
              className="text-[13px] font-medium leading-6 text-foreground"
            >
              {t('SKILL_DRAFT_PROMPT_LABEL') || 'Describe the skill in one line'}
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="skill-draft-prompt"
                value={draftPrompt}
                onChange={(event) => setDraftPrompt(event.target.value)}
                placeholder={
                  t('SKILL_DRAFT_PROMPT_PLACEHOLDER') ||
                  'Summarize recent PRs into a weekly status report'
                }
                className="h-11 flex-1 rounded-[0.5rem] border-border bg-background px-4 text-[13px] shadow-none"
              />
              <Button
                type="button"
                className="h-11 shrink-0 gap-2 rounded-[0.5rem] px-4 text-[13px] font-semibold"
                onClick={() => void handleGenerateDraft()}
                disabled={!draftPrompt.trim() || isDrafting}
              >
                {isDrafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {t('SKILL_DRAFT_GENERATE') || 'Generate'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-11 shrink-0 rounded-[0.5rem] px-3 text-[13px]"
                onClick={() => setShowDraftPrompt(false)}
              >
                {t('SKILL_DRAFT_SKIP') || 'Write manually'}
              </Button>
            </div>
          </div>
        ) : null}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
              <div className="grid gap-5">
                <div className="grid gap-4 rounded-[0.75rem] border border-border/70 bg-muted/10 p-4 lg:grid-cols-[84px_minmax(0,1fr)] lg:p-5">
                  <div className="flex h-[84px] w-[84px] items-center justify-center rounded-[0.75rem] border border-dashed border-border bg-background text-muted-foreground">
                    {isEditing ? <Pencil className="h-7 w-7" /> : <Sparkles className="h-7 w-7" />}
                  </div>

                  <div className="grid gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-3">
                            <FormLabel className="text-[13px] font-medium leading-6 text-foreground">
                              {t('CREATE_SKILL_NAME_LABEL') || 'Skill name'}
                            </FormLabel>
                            <span className="text-xs text-muted-foreground">{field.value.length}/64</span>
                          </div>
                          <FormControl>
                            <Input
                              placeholder={
                                t('CREATE_SKILL_NAME_PLACEHOLDER') || 'weekly-status-report'
                              }
                              maxLength={64}
                              className="h-11 rounded-[0.5rem] border-border bg-background px-4 text-[13px] shadow-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="displayName"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-3">
                            <FormLabel className="text-[13px] font-medium leading-6 text-foreground">
                              {t('CREATE_SKILL_DISPLAY_NAME_LABEL') || 'Display name'}
                            </FormLabel>
                            <span className="text-xs text-muted-foreground">{field.value.length}/128</span>
                          </div>
                          <FormControl>
                            <Input
                              placeholder={t('CREATE_SKILL_DISPLAY_NAME_PLACEHOLDER') || 'Weekly Status Report'}
                              maxLength={128}
                              className="h-11 rounded-[0.5rem] border-border bg-background px-4 text-[13px] shadow-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription className="text-[13px] leading-6 text-muted-foreground">
                            {t('CREATE_SKILL_DISPLAY_NAME_HELPER') ||
                              'Human-readable name shown in the UI. Falls back to the skill name when left blank.'}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-3">
                            <FormLabel className="text-[13px] font-medium leading-6 text-foreground">
                              {t('DESCRIPTION') || 'Description'}
                            </FormLabel>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleOptimizeField('description')}
                                disabled={!field.value.trim() || isOptimizing}
                                className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80 disabled:opacity-40"
                              >
                                {optimizingField === 'description' ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Wand2 className="h-3 w-3" />
                                )}
                                {t('SKILL_OPTIMIZE_WITH_AI') || 'Optimize with AI'}
                              </button>
                              <span className="text-xs text-muted-foreground">{field.value.length}/1024</span>
                            </div>
                          </div>
                          <FormControl>
                            <Textarea
                              placeholder={
                                t('CREATE_SKILL_DESCRIPTION_PLACEHOLDER') ||
                                'Generate weekly status reports from recent work. Use when asked for updates or progress summaries.'
                              }
                              maxLength={1024}
                              className="min-h-[124px] rounded-[0.5rem] border-border px-4 py-3 text-[13px] shadow-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription className="text-[13px] leading-6 text-muted-foreground">
                            {t('CREATE_SKILL_DESCRIPTION_HELPER') ||
                              'The AI decides whether to use this skill based on the description, so make it specific and clear.'}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="grid gap-4 rounded-[0.75rem] border border-border/70 bg-background p-4 lg:p-5">
                  <FormField
                    control={form.control}
                    name="visibility"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <SkillVisibilityField
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium leading-6 text-foreground">
                          {t('CREATE_SKILL_TAGS_LABEL') || 'Tags'}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={
                              t('CREATE_SKILL_TAGS_PLACEHOLDER') || 'example, support, onboarding'
                            }
                            className="h-11 rounded-[0.5rem] border-border bg-background px-4 text-[13px] shadow-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-[13px] leading-6 text-muted-foreground">
                          {t('CREATE_SKILL_TAGS_HELPER') ||
                            'Separate multiple tags with commas.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="requiredToolIds"
                    render={({ field }) => {
                      const selectedTools = field.value
                        .map((toolId) => toolsById.get(toolId) || null)
                        .filter((tool): tool is ITool => !!tool);
                      const unknownSelectedToolIds = field.value.filter((toolId) => !toolsById.has(toolId));

                      const toggleTool = (toolId: string) => {
                        const nextIds = field.value.includes(toolId)
                          ? field.value.filter((value) => value !== toolId)
                          : [...field.value, toolId];
                        field.onChange(nextIds);
                      };

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between gap-3">
                            <FormLabel className="text-[13px] font-medium leading-6 text-foreground">
                              {t('CREATE_SKILL_TOOLS_LABEL') || 'Tools'}
                            </FormLabel>
                            <span className="text-xs text-muted-foreground">
                              {t('CREATE_SKILL_TOOLS_SELECTED', { count: field.value.length }) ||
                                `${field.value.length} selected`}
                            </span>
                          </div>
                          <Popover modal open={isToolsOpen} onOpenChange={setIsToolsOpen}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  type="button"
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={isToolsOpen}
                                  className="h-auto min-h-11 w-full justify-between rounded-[0.5rem] border-border bg-background px-4 py-3 text-left text-[13px] font-normal shadow-none hover:bg-background"
                                >
                                  <span className="truncate text-muted-foreground">
                                    {field.value.length > 0
                                      ? field.value.length === 1
                                        ? t('CREATE_SKILL_TOOLS_PICKER_ONE') ||
                                        '1 tool selected'
                                        : t('CREATE_SKILL_TOOLS_PICKER_MANY', {
                                          count: field.value.length,
                                        }) || `${field.value.length} tools selected`
                                      : t('CREATE_SKILL_TOOLS_PICKER_PLACEHOLDER') ||
                                      'Select tools'}
                                  </span>
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="min-h-[360px] p-0"
                              style={{ width: 'var(--radix-popover-trigger-width)' }}
                              onWheel={(event) => event.stopPropagation()}
                            >
                              <Command className="overflow-hidden">
                                <CommandInput
                                  placeholder={
                                    t('CREATE_SKILL_TOOLS_SEARCH') || 'Search tools...'
                                  }
                                />
                                <Tabs value={activeToolTab} onValueChange={(value) => setActiveToolTab(value as 'api' | 'mcp_server')}>
                                  <div className="border-b border-border px-3 py-2">
                                    <TabsList className="grid h-8 w-full grid-cols-2 rounded-[6px] border border-border bg-muted/40 p-0.5 shadow-none">
                                      <TabsTrigger value="api" className="h-full flex-1 rounded-[4px] px-0 text-xs font-medium">
                                        {t('TOOLS_TAB_API') || 'API'}
                                      </TabsTrigger>
                                      <TabsTrigger
                                        value="mcp_server"
                                        className="h-full flex-1 rounded-[4px] px-0 text-xs font-medium"
                                      >
                                        {t('TOOLS_TAB_MCP') || 'MCP'}
                                      </TabsTrigger>
                                    </TabsList>
                                  </div>
                                </Tabs>
                                <CommandList className="min-h-[270px] max-h-[320px] overscroll-contain">
                                  <CommandEmpty>
                                    {isVisibleToolsLoading
                                      ? t('CREATE_SKILL_TOOLS_LOADING') || 'Loading tools...'
                                      : t('CREATE_SKILL_TOOLS_NOT_FOUND') || 'No tools found.'}
                                  </CommandEmpty>
                                  <CommandGroup
                                    heading={t('CREATE_SKILL_TOOLS_AVAILABLE') || 'Available tools'}
                                  >
                                    {isVisibleToolsLoading ? (
                                      <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {t('CREATE_SKILL_TOOLS_LOADING') || 'Loading tools...'}
                                      </div>
                                    ) : (
                                      visibleTools.map((tool) => {
                                        const isSelected = field.value.includes(tool.id);

                                        return (
                                          <CommandItem
                                            key={tool.id}
                                            value={`${tool.name} ${tool.description || ''} ${tool.type}`}
                                            onSelect={() => toggleTool(tool.id)}
                                            className="items-start gap-3 py-2.5"
                                          >
                                            <div
                                              className={cn(
                                                'mt-0.5 flex h-4 w-4 items-center justify-center rounded-[4px] border border-primary',
                                                isSelected ? 'bg-primary text-primary-foreground' : 'bg-background text-transparent'
                                              )}
                                            >
                                              <Check className="h-3 w-3" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                              <div className="truncate text-sm font-medium">{tool.name}</div>
                                              <div className="line-clamp-2 text-xs text-muted-foreground">
                                                {tool.description ||
                                                  t('CREATE_SKILL_TOOL_NO_DESCRIPTION') ||
                                                  'No description'}
                                              </div>
                                            </div>
                                            <Badge variant="outline" className="ml-2 shrink-0 text-[10px] font-normal">
                                              {getToolTypeLabel(tool.type, t)}
                                            </Badge>
                                          </CommandItem>
                                        );
                                      })
                                    )}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <FormDescription className="text-[13px] leading-6 text-muted-foreground">
                            {t('CREATE_SKILL_TOOLS_HELPER') ||
                              'Choose one or more API tools or MCP servers to include in this skill.'}
                          </FormDescription>
                          {field.value.length > 0 ? (
                            <div className="flex flex-wrap gap-2 pt-1">
                              {selectedTools.map((tool) => (
                                <div
                                  key={tool.id}
                                  className="flex items-center gap-1.5 rounded-full border border-[#d7e6db] bg-[#eef7f1] px-2.5 py-1 text-[11px] font-medium text-[#274a36] dark:border-white/12 dark:bg-[#2a2a2d] dark:text-[#f2f2f3]"
                                >
                                  <Wrench className="h-3 w-3" />
                                  <span>{tool.name}</span>
                                  <span className="rounded-full border border-current/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.02em]">
                                    {tool.type === 'mcp_server' ? 'MCP' : 'API'}
                                  </span>
                                  <button
                                    type="button"
                                    className="ml-0.5 inline-flex h-4 w-4 items-center justify-center text-inherit outline-none"
                                    onClick={() => toggleTool(tool.id)}
                                    aria-label={
                                      t('CREATE_SKILL_REMOVE_TOOL', { name: tool.name }) ||
                                      `Remove ${tool.name}`
                                    }
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                              {unknownSelectedToolIds.map((toolId) => (
                                <div
                                  key={toolId}
                                  className="flex items-center gap-1 rounded-full border border-[#d7e6db] bg-[#eef7f1] px-2.5 py-1 text-[11px] font-medium text-[#274a36] dark:border-white/12 dark:bg-[#2a2a2d] dark:text-[#f2f2f3]"
                                >
                                  <Wrench className="h-3 w-3" />
                                  <span>{toolId}</span>
                                  <button
                                    type="button"
                                    className="ml-0.5 inline-flex h-4 w-4 items-center justify-center text-inherit outline-none"
                                    onClick={() => toggleTool(toolId)}
                                    aria-label={
                                      t('CREATE_SKILL_REMOVE_TOOL', { name: toolId }) ||
                                      `Remove ${toolId}`
                                    }
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rounded-[0.5rem] border border-dashed border-border bg-muted/20 px-4 py-4 text-sm text-muted-foreground">
                              {t('CREATE_SKILL_NO_TOOLS_SELECTED') ||
                                'No tools selected yet.'}
                            </div>
                          )}
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                </div>

                <div className="rounded-[0.75rem] border border-border/70 bg-background p-4 lg:p-5">
                  <FormField
                    control={form.control}
                    name="instructions"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between gap-3">
                          <FormLabel className="text-[13px] font-medium leading-6 text-foreground">
                            {t('CREATE_SKILL_INSTRUCTIONS_LABEL') || 'Instructions'}
                          </FormLabel>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void handleOptimizeField('instructions')}
                              disabled={!field.value.trim() || isOptimizing}
                              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:opacity-80 disabled:opacity-40"
                            >
                              {optimizingField === 'instructions' ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Wand2 className="h-3 w-3" />
                              )}
                              {t('SKILL_OPTIMIZE_WITH_AI') || 'Optimize with AI'}
                            </button>
                            <span className="text-xs text-muted-foreground">{field.value.length}/50000</span>
                          </div>
                        </div>
                        <FormControl>
                          <div className="rounded-[0.5rem] border border-border bg-muted/10">
                            <MarkdownEditor
                              value={field.value}
                              onChange={field.onChange}
                              placeholder={
                                t('CREATE_SKILL_INSTRUCTIONS_PLACEHOLDER') ||
                                '## When to use\n...\n\n## How to respond\n1. ...'
                              }
                              className="mx-0 border-0 bg-transparent"
                              contentClassName="min-h-[320px]"
                              maxLength={50000}
                              showCharacterCount={false}
                            />
                          </div>
                        </FormControl>
                        <FormDescription className="text-[13px] leading-6 text-muted-foreground">
                          {t('CREATE_SKILL_INSTRUCTIONS_HELPER') ||
                            'Instructions are saved as markdown and shown as the main SKILL.md file.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="px-4 py-4">
              <div className="flex w-full items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 rounded-[6px] px-4 text-[13px] font-medium text-foreground shadow-none hover:bg-transparent hover:text-foreground"
                  onClick={() => handleOpenChange(false)}
                >
                  {t('CANCEL') || 'Cancel'}
                </Button>
                <Button type="submit" className="h-8 rounded-[6px] px-4 text-[13px] font-semibold" disabled={isPending || (isEditing && !canEditSkill)}>
                  {isEditing ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {isPending
                    ? isEditing
                      ? t('CREATE_SKILL_SAVING') || 'Saving...'
                      : t('CREATE_SKILL_CREATING') || 'Creating...'
                    : isEditing
                      ? t('SAVE') || 'Save'
                      : t('CREATE_SKILL_CREATE') || 'Create'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
